import * as THREE from '@phdsoft/three';
import { RepairGeometryUtils } from './repair-geometry-utils';
import { RepairOrthoFrame } from './repair-ortho-frame';

export class RepairGeometryClipper {
    private planes: THREE.Plane[] = [];
    private circleCenter: THREE.Vector3 | null = null;
    private circleRadius: number | null = null;
    private shapeType: 'rectangle' | 'circle' | 'ellipse' = 'rectangle';
    private ellipseParams: { centerX: number, centerY: number, radiusX: number, radiusY: number } | null = null;
    private camera: THREE.Camera;
    private orthoFrame: RepairOrthoFrame | null = null;
    private orthoComplement = false;

    constructor(camera: THREE.Camera) {
        this.camera = camera;
    }

    public get isOrtho(): boolean {
        return this.orthoFrame != null || this.orthoComplement;
    }

    /** Orthographic mode: planes/inside tests come from the prism along the element normal. */
    public initOrtho(frame: RepairOrthoFrame) {
        this.orthoFrame = frame;
        this.shapeType = frame.shapeType;
        this.planes = frame.getSidePlanes();
    }

    /**
     * Orthographic rectangle from explicit world corners (cut-mode complement rectangles).
     * Planes are sign-normalized against the rectangle's own center.
     */
    public initFromOrthoRectCorners(corners: THREE.Vector3[], n: THREE.Vector3) {
        this.shapeType = 'rectangle';
        if (corners.length < 4) return;
        this.orthoComplement = true;
        const center = new THREE.Vector3();
        corners.forEach((c) => center.add(c));
        center.divideScalar(corners.length);
        this.planes = RepairOrthoFrame.prismPlanes(corners, n, center);
    }

    public initFromRectangle(vertices: THREE.Vector3[]) {
        this.shapeType = 'rectangle';
        if (vertices.length < 4) return;
        this.initPlanesFromRectVertices(vertices);
    }

    public initFromCircle(
        centerX: number,
        centerY: number,
        radius: number,
        distz: number = 1
    ) {
        this.shapeType = 'circle';

        const centerLocal = RepairGeometryUtils.screenToLocal(this.camera, centerX, centerY, distz);
        this.circleCenter = this.camera.localToWorld(centerLocal.clone());

        const angles = [0, Math.PI / 4, Math.PI / 2, 3 * Math.PI / 4, Math.PI, 5 * Math.PI / 4, 3 * Math.PI / 2, 7 * Math.PI / 4];
        let maxRadius = 0;
        for (const angle of angles) {
            const edgeX = centerX + radius * Math.cos(angle);
            const edgeY = centerY + radius * Math.sin(angle);
            const edgePointLocal = RepairGeometryUtils.screenToLocal(this.camera, edgeX, edgeY, distz);
            const edgePointWorld = this.camera.localToWorld(edgePointLocal.clone());
            const dist = this.circleCenter.distanceTo(edgePointWorld);
            if (dist > maxRadius) {
                maxRadius = dist;
            }
        }
        this.circleRadius = maxRadius;

        this.generatePlanesFromCircleBBox(centerX, centerY, radius, distz);
    }

    public initFromEllipse(
        centerX: number,
        centerY: number,
        radiusX: number,
        radiusY: number,
        distz: number = 1
    ) {
        this.shapeType = 'ellipse';
        this.ellipseParams = { centerX, centerY, radiusX, radiusY };
        this.generatePlanesFromEllipseBBox(centerX, centerY, radiusX, radiusY, distz);
    }

    public initFromVerticesForCircle(vertices: THREE.Vector3[]) {
        this.shapeType = 'circle';
        if (vertices.length < 1) return;

        this.circleCenter = vertices[0];
        const circleVertices = vertices.slice(1);

        if (circleVertices.length > 0) {
            let maxRadius = 0;
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;

            for (const v of circleVertices) {
                const dist = this.circleCenter.distanceTo(v);
                if (dist > maxRadius) {
                    maxRadius = dist;
                }
                minX = Math.min(minX, v.x); maxX = Math.max(maxX, v.x);
                minY = Math.min(minY, v.y); maxY = Math.max(maxY, v.y);
                minZ = Math.min(minZ, v.z); maxZ = Math.max(maxZ, v.z);
            }
            this.circleRadius = maxRadius;

            // Generate planes from BBox
            const rect = [
                new THREE.Vector3(minX, maxY, (minZ + maxZ) / 2), // top-left
                new THREE.Vector3(maxX, maxY, (minZ + maxZ) / 2), // top-right
                new THREE.Vector3(maxX, minY, (minZ + maxZ) / 2), // bottom-right
                new THREE.Vector3(minX, minY, (minZ + maxZ) / 2), // bottom-left
            ];
            this.initPlanesFromRectVertices(rect);
        }
    }

    public getPlanes(): THREE.Plane[] {
        return this.planes;
    }

    public isPointInside(
        point: THREE.Vector3,
        screenParams?: {
            centerX: number,
            centerY: number,
            radius: number,
            radiusX?: number,
            radiusY?: number,
            objectWorldMatrix?: THREE.Matrix4,
            objectBoundingBox?: THREE.Box3,
            rectangleCorners?: THREE.Vector3[]
        }
    ): boolean {
        if (this.orthoFrame) {
            // ortho mode ignores screen-space params entirely
            return this.orthoFrame.isInside(point);
        }
        if (this.shapeType === 'circle' && screenParams) {
            return RepairGeometryUtils.isPointInsideCircleScreenSpace(
                this.camera,
                point,
                screenParams.centerX,
                screenParams.centerY,
                screenParams.radius
            );
        } else if (this.shapeType === 'circle' && this.circleCenter && this.circleRadius) {
            return RepairGeometryUtils.isPointInsideSphere(point, this.circleCenter, this.circleRadius);
        } else if (this.shapeType === 'ellipse') {
            if (screenParams && screenParams.radiusX && screenParams.radiusY) {
                return RepairGeometryUtils.isPointInsideEllipseScreenSpace(
                    this.camera,
                    point,
                    screenParams.centerX,
                    screenParams.centerY,
                    screenParams.radiusX,
                    screenParams.radiusY
                );
            } else if (this.ellipseParams) {
                return RepairGeometryUtils.isPointInsideEllipseScreenSpace(
                    this.camera,
                    point,
                    this.ellipseParams.centerX,
                    this.ellipseParams.centerY,
                    this.ellipseParams.radiusX,
                    this.ellipseParams.radiusY
                );
            }
            return false;
        } else if (this.shapeType === 'rectangle' && screenParams?.rectangleCorners && screenParams.rectangleCorners.length === 4) {
            // Use screen-space check for rectangles on curved surfaces
            return RepairGeometryUtils.isPointInsideRectangleScreenSpace(
                this.camera,
                point,
                screenParams.rectangleCorners
            );
        } else {
            // Fallback to plane-based check
            return this.planes.every((pl) => pl.distanceToPoint(point) >= -0.001) ||
                this.planes.every((pl) => pl.distanceToPoint(point) <= 0);
        }
    }

    private isPointInsideSmallCircle(
        point: THREE.Vector3,
        screenRadius: number,
        objectWorldMatrix: THREE.Matrix4,
        boundingBox?: THREE.Box3
    ): boolean {
        if (!boundingBox) return false;

        const bboxCenter = boundingBox.getCenter(new THREE.Vector3()).applyMatrix4(objectWorldMatrix);
        const cameraPos = this.camera.position.clone();
        const depth = cameraPos.distanceTo(bboxCenter);

        const fovY = (this.camera as any).isPerspectiveCamera && (this.camera as any).fov
            ? THREE.MathUtils.degToRad((this.camera as any).fov)
            : Math.PI / 2;

        const halfScreenY = window.innerHeight / 2;
        const ndcRadiusY = screenRadius / halfScreenY;
        const worldRadius = depth * Math.tan(fovY / 2) * ndcRadiusY;

        const cameraDir = this.camera.getWorldDirection(new THREE.Vector3()).normalize();
        const sphereCenter = cameraPos.clone().add(cameraDir.multiplyScalar(depth));

        return RepairGeometryUtils.isPointInsideSphere(point, sphereCenter, worldRadius);
    }

    private generatePlanesFromCircleBBox(centerX: number, centerY: number, radius: number, distz: number) {
        const minX = centerX - radius;
        const maxX = centerX + radius;
        const minY = centerY - radius;
        const maxY = centerY + radius;

        const v1 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, minX, maxY, distz));
        const v2 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, maxX, maxY, distz));
        const v3 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, maxX, minY, distz));
        const v4 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, minX, minY, distz));

        this.initPlanesFromRectVertices([v1, v2, v3, v4]);
    }

    private generatePlanesFromEllipseBBox(centerX: number, centerY: number, radiusX: number, radiusY: number, distz: number) {
        const minX = centerX - radiusX;
        const maxX = centerX + radiusX;
        const minY = centerY - radiusY;
        const maxY = centerY + radiusY;

        const v1 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, minX, maxY, distz));
        const v2 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, maxX, maxY, distz));
        const v3 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, maxX, minY, distz));
        const v4 = this.camera.localToWorld(RepairGeometryUtils.screenToLocal(this.camera, minX, minY, distz));

        this.initPlanesFromRectVertices([v1, v2, v3, v4]);
    }

    private initPlanesFromRectVertices(vertices: THREE.Vector3[]) {
        if (vertices.length < 4) return;
        this.planes = [
            this.createPlaneFromCamera(vertices[1], vertices[0]),
            this.createPlaneFromCamera(vertices[2], vertices[1]),
            this.createPlaneFromCamera(vertices[3], vertices[2]),
            this.createPlaneFromCamera(vertices[0], vertices[3])
        ];
    }

    private createPlaneFromCamera(p1: THREE.Vector3, p2: THREE.Vector3): THREE.Plane {
        return new THREE.Plane().setFromCoplanarPoints(
            this.camera.position,
            p1,
            p2
        );
    }
}
